/**
 * Required Reserves helpers
 * - CashInHand: UseForReserve checkbox (no downpayment)
 * - Verified -> defaults UseForReserve
 * - Reserve/Downpayment are mutually exclusive
 * - New math uses two percents: AU % and Reserve %
 *
 * `selectedProduct` throughout this file is a canonical loan-program code
 * (CX.LOAN.PRODUCT.NAME as translated by src/utils/loanProgramUtil.js), not a
 * brand name.
 */

import { LOAN_PROGRAM_CODES } from "../config/organizations";
import { isLoanProgram } from "./loanProgramUtil";

export const ACCOUNT_TYPE_LABEL = {
    CheckingAccount: "Checking Account",
    SavingsAccount: "Savings Account",
    MoneyMarketFund: "Money Market Fund",
    MutualFund: "Mutual Funds",
    Stock: "Stock",
    Bond: "Bond",
    RetirementFund: "Retirement Funds",
    RetirementFunds: "Retirement Funds",
    TrustAccount: "Trust Account",
    CertificateOfDepositTimeDeposit: "Certificate of Deposit",
    BridgeLoanNotDeposited: "Bridge Loan Proceeds",
    IndividualDevelopmentAccount: "Individual Development Account",
    LifeInsurance: "Life Insurance- Cash Value"
};

export const SUPPORTED_ACCOUNT_TYPES = [
    "CheckingAccount",
    "SavingsAccount",
    "MoneyMarketFund",
    "MutualFund",
    "Stock",
    "Bond",
    "RetirementFund",
    "RetirementFunds",
    "TrustAccount",
    "CertificateOfDepositTimeDeposit",
    "BridgeLoanNotDeposited",
    "IndividualDevelopmentAccount",
    "LifeInsurance"
];

export const ACCOUNT_TYPE_GROUPS = [
    {
        groupName: "Checking, Savings, & Money Market Accounts",
        types: ["CheckingAccount", "SavingsAccount", "MoneyMarketFund"]
    },
    {
        groupName: "Stocks & Bonds Accounts",
        types: ["MutualFund", "Stock", "Bond"]
    },
    {
        groupName: "Retirement Funds",
        types: ["RetirementFund", "RetirementFunds"]
    },
    {
        groupName: "Other",
        types: [
            "TrustAccount",
            "CertificateOfDepositTimeDeposit",
            "BridgeLoanNotDeposited",
            "IndividualDevelopmentAccount",
            "LifeInsurance"
        ]
    }
];

export const ASSET_TYPE_LABEL_MAP = {
    GiftOfCash: "Gift of Cash",
    GiftOfPropertyEquity: "Gift Of Property Equity",
    Grant: "Grant",

    PendingNetSaleProceedsFromRealEstateAsset: "Proceeds from Sale of Real Estate",
    PendingNetSaleProceedsFromRealEstateAssets: "Proceeds from Sale of Real Estate", // endpoint sometimes returns plural
    ProceedsFromSaleOfNonRealEstateAsset: "Proceeds from the sale of Non-Real Estate asset",
    Annuity: "Annuity",
    ProceedsFromSecuredLoan: "Secured Borrowed Funds",
    Other: "Other",
    EarnestMoney: "EMD",
    EmployerAssistedHousing: "Empoyer Assistance/ Relocation Funds",
    RelocationFunds: "Empoyer Assistance/ Relocation Funds",
    LotEquity: "Lot Equity",
    LeasePurchaseCredit: "Rent Credit"
};


/*
 * Product-driven percentages, keyed by canonical loan-program code rather
 * than by brand name, so the same rule applies whichever company's Encompass
 * the loan came from. A product with no entry here yields 0 - which is what
 * gates the calculator in isEligibleForReservesCalculator() - so adding a
 * product to src/config/organizations.js is not enough on its own; its rules
 * have to be added here too.
 *
 * TODO: PROGRAM_012 (SKY PLUS, SkyTPO only) has no rules yet. Until they are
 * supplied it is absent from all three tables below, so the reserves
 * calculator reports it as unavailable rather than silently computing 0%.
 */
export const CASH_IN_HAND_PERCENT_BY_PRODUCT_SELECT = {
    [LOAN_PROGRAM_CODES.PROGRAM_002]: 1.0, // ACCELERATOR / BREEZY
    [LOAN_PROGRAM_CODES.PROGRAM_004]: 1.0, // ACTIVATOR / SUNNY
    [LOAN_PROGRAM_CODES.PROGRAM_006]: 1.0, // ACTIVATOR ITIN / SUNNY ITIN
    [LOAN_PROGRAM_CODES.PROGRAM_003]: 1.0, // ACCELERATOR INCOME / BREEZY INCOME
    [LOAN_PROGRAM_CODES.PROGRAM_005]: 1.0, // ACCELERATOR ITIN / BREEZY ITIN
    [LOAN_PROGRAM_CODES.PROGRAM_007]: 1.0, // AMBASSADOR / PASSPORT
    [LOAN_PROGRAM_CODES.PROGRAM_008]: 0.0, // SUPER JUMBO / STARRY
    [LOAN_PROGRAM_CODES.PROGRAM_001]: 1.0, // ALLY (ChampsTPO only)
    [LOAN_PROGRAM_CODES.PROGRAM_009]: 0.0  // ACTIVATOR PRIME / SUNNY SELECT
};

export function getCashInHandAllowedPercent(selectedProduct) {
    const sel = String(selectedProduct || "").trim().toUpperCase();
    const pct = CASH_IN_HAND_PERCENT_BY_PRODUCT_SELECT[sel];
    return Number.isFinite(Number(pct)) ? Number(pct) : 0;
}

// Two percents per account type:
// - auPercent: percent used to back-calc Verified Asset from AU Amount
// - reservePercent: percent applied to Verified Asset to compute Allowed Reserves
export const AU_RESERVE_PERCENT_CONFIG_BY_PRODUCT_SELECT = {
    // ACCELERATOR / BREEZY
    [LOAN_PROGRAM_CODES.PROGRAM_002]: {
        CheckingAccount: { auPercent: 1.0, reservePercent: 1.0 },
        SavingsAccount: { auPercent: 1.0, reservePercent: 1.0 },
        MoneyMarketFund: { auPercent: 1.0, reservePercent: 1.0 },
        MutualFund: { auPercent: 1.0, reservePercent: 1.0 },
        MutualFunds: { auPercent: 1.0, reservePercent: 1.0 },
        Stock: { auPercent: 1.0, reservePercent: 1.0 },
        Bond: { auPercent: 1.0, reservePercent: 1.0 },
        RetirementFund: { auPercent: { under: 0.7, over: 1.0 }, reservePercent: { under: 0.8, over: 1.0 } },
        RetirementFunds: { auPercent: { under: 0.7, over: 1.0 }, reservePercent: { under: 0.8, over: 1.0 } },

        CertificateOfDepositTimeDeposit: { auPercent: 1.0, reservePercent: 1.0 },
        BridgeLoanNotDeposited: { auPercent: 1.0, reservePercent: 1.0 },
        IndividualDevelopmentAccount: { auPercent: 1.0, reservePercent: 1.0 },
        TrustAccount: { auPercent: 1.0, reservePercent: 1.0 },
        LifeInsurance: { auPercent: 1.0, reservePercent: 1.0 }
    },

    // ACCELERATOR INCOME / BREEZY INCOME
    [LOAN_PROGRAM_CODES.PROGRAM_003]: {
        CheckingAccount: { auPercent: 1.0, reservePercent: 1.0 },
        SavingsAccount: { auPercent: 1.0, reservePercent: 1.0 },
        MoneyMarketFund: { auPercent: 1.0, reservePercent: 1.0 },
        MutualFund: { auPercent: 1.0, reservePercent: 1.0 },
        MutualFunds: { auPercent: 1.0, reservePercent: 1.0 },
        Stock: { auPercent: 1.0, reservePercent: 1.0 },
        Bond: { auPercent: 1.0, reservePercent: 1.0 },
        RetirementFund: { auPercent: { under: 0.7, over: 1.0 }, reservePercent: { under: 0.8, over: 1.0 } },
        RetirementFunds: { auPercent: { under: 0.7, over: 1.0 }, reservePercent: { under: 0.8, over: 1.0 } },

        CertificateOfDepositTimeDeposit: { auPercent: 1.0, reservePercent: 1.0 },
        BridgeLoanNotDeposited: { auPercent: 1.0, reservePercent: 1.0 },
        IndividualDevelopmentAccount: { auPercent: 1.0, reservePercent: 1.0 },
        TrustAccount: { auPercent: 1.0, reservePercent: 1.0 },
        LifeInsurance: { auPercent: 1.0, reservePercent: 1.0 }
    },

    // ACCELERATOR ITIN / BREEZY ITIN
    [LOAN_PROGRAM_CODES.PROGRAM_005]: {
        CheckingAccount: { auPercent: 1.0, reservePercent: 1.0 },
        SavingsAccount: { auPercent: 1.0, reservePercent: 1.0 },
        MoneyMarketFund: { auPercent: 1.0, reservePercent: 1.0 },
        MutualFund: { auPercent: 1.0, reservePercent: 1.0 },
        MutualFunds: { auPercent: 1.0, reservePercent: 1.0 },
        Stock: { auPercent: 1.0, reservePercent: 1.0 },
        Bond: { auPercent: 1.0, reservePercent: 1.0 },
        RetirementFund: { auPercent: { under: 0.7, over: 1.0 }, reservePercent: { under: 0.8, over: 1.0 } },
        RetirementFunds: { auPercent: { under: 0.7, over: 1.0 }, reservePercent: { under: 0.8, over: 1.0 } },

        CertificateOfDepositTimeDeposit: { auPercent: 1.0, reservePercent: 1.0 },
        BridgeLoanNotDeposited: { auPercent: 1.0, reservePercent: 1.0 },
        IndividualDevelopmentAccount: { auPercent: 1.0, reservePercent: 1.0 },
        TrustAccount: { auPercent: 1.0, reservePercent: 1.0 },
        LifeInsurance: { auPercent: 1.0, reservePercent: 1.0 }
    },

    // ACTIVATOR / SUNNY
    [LOAN_PROGRAM_CODES.PROGRAM_004]: {
        CheckingAccount: { auPercent: 1.0, reservePercent: 1.0 },
        SavingsAccount: { auPercent: 1.0, reservePercent: 1.0 },
        MoneyMarketFund: { auPercent: 1.0, reservePercent: 1.0 },
        MutualFund: { auPercent: 1.0, reservePercent: 1.0 },
        MutualFunds: { auPercent: 1.0, reservePercent: 1.0 },
        Stock: { auPercent: 1.0, reservePercent: 1.0 },
        Bond: { auPercent: 1.0, reservePercent: 1.0 },
        RetirementFund: { auPercent: { under: 0.7, over: 1.0 }, reservePercent: { under: 0.8, over: 1.0 } },
        RetirementFunds: { auPercent: { under: 0.7, over: 1.0 }, reservePercent: { under: 0.8, over: 1.0 } },

        CertificateOfDepositTimeDeposit: { auPercent: 1.0, reservePercent: 1.0 },
        BridgeLoanNotDeposited: { auPercent: 1.0, reservePercent: 1.0 },
        IndividualDevelopmentAccount: { auPercent: 1.0, reservePercent: 1.0 },
        TrustAccount: { auPercent: 1.0, reservePercent: 1.0 },
        LifeInsurance: { auPercent: 1.0, reservePercent: 1.0 }
    },

    // AMBASSADOR / PASSPORT
    [LOAN_PROGRAM_CODES.PROGRAM_007]: {
        CheckingAccount: { auPercent: 1.0, reservePercent: 1.0 },
        SavingsAccount: { auPercent: 1.0, reservePercent: 1.0 },
        MoneyMarketFund: { auPercent: 1.0, reservePercent: 1.0 },
        MutualFund: { auPercent: 1.0, reservePercent: 1.0 },
        MutualFunds: { auPercent: 1.0, reservePercent: 1.0 },
        Stock: { auPercent: 1.0, reservePercent: 1.0 },
        Bond: { auPercent: 1.0, reservePercent: 1.0 },
        RetirementFund: { auPercent: { under: 0.7, over: 1.0 }, reservePercent: { under: 0.8, over: 1.0 } },
        RetirementFunds: { auPercent: { under: 0.7, over: 1.0 }, reservePercent: { under: 0.8, over: 1.0 } },

        CertificateOfDepositTimeDeposit: { auPercent: 1.0, reservePercent: 1.0 },
        BridgeLoanNotDeposited: { auPercent: 1.0, reservePercent: 1.0 },
        IndividualDevelopmentAccount: { auPercent: 1.0, reservePercent: 1.0 },
        TrustAccount: { auPercent: 1.0, reservePercent: 1.0 },
        LifeInsurance: { auPercent: 1.0, reservePercent: 1.0 }
    },

    // ACTIVATOR ITIN / SUNNY ITIN
    [LOAN_PROGRAM_CODES.PROGRAM_006]: {
        CheckingAccount: { auPercent: 1.0, reservePercent: 1.0 },
        SavingsAccount: { auPercent: 1.0, reservePercent: 1.0 },
        MoneyMarketFund: { auPercent: 1.0, reservePercent: 1.0 },
        MutualFund: { auPercent: 1.0, reservePercent: 1.0 },
        MutualFunds: { auPercent: 1.0, reservePercent: 1.0 },
        Stock: { auPercent: 1.0, reservePercent: 1.0 },
        Bond: { auPercent: 1.0, reservePercent: 1.0 },
        RetirementFund: { auPercent: { under: 0.7, over: 1.0 }, reservePercent: { under: 0.8, over: 1.0 } },
        RetirementFunds: { auPercent: { under: 0.7, over: 1.0 }, reservePercent: { under: 0.8, over: 1.0 } },

        CertificateOfDepositTimeDeposit: { auPercent: 1.0, reservePercent: 1.0 },
        BridgeLoanNotDeposited: { auPercent: 1.0, reservePercent: 1.0 },
        IndividualDevelopmentAccount: { auPercent: 1.0, reservePercent: 1.0 },
        TrustAccount: { auPercent: 1.0, reservePercent: 1.0 },
        LifeInsurance: { auPercent: 1.0, reservePercent: 1.0 }
    },

    // SUPER JUMBO / STARRY
    [LOAN_PROGRAM_CODES.PROGRAM_008]: {
        CheckingAccount: { auPercent: 1.0, reservePercent: 1.0 },
        SavingsAccount: { auPercent: 1.0, reservePercent: 1.0 },
        MoneyMarketFund: { auPercent: 1.0, reservePercent: 1.0 },
        MutualFund: { auPercent: 0.8, reservePercent: 0.8 },
        MutualFunds: { auPercent: 0.8, reservePercent: 0.8 },
        Stock: { auPercent: 0.8, reservePercent: 0.8 },
        Bond: { auPercent: 0.8, reservePercent: 0.8 },
        RetirementFund: { auPercent: { under: 0.6, over: 0.7 }, reservePercent: { under: 0.6, over: 0.7 } },
        RetirementFunds: { auPercent: { under: 0.6, over: 0.7 }, reservePercent: { under: 0.6, over: 0.7 } },

        CertificateOfDepositTimeDeposit: { auPercent: 1.0, reservePercent: 1.0 },
        BridgeLoanNotDeposited: { auPercent: 1.0, reservePercent: 1.0 },
        IndividualDevelopmentAccount: { auPercent: 1.0, reservePercent: 1.0 },
        TrustAccount: { auPercent: 1.0, reservePercent: 1.0 },
        LifeInsurance: { auPercent: 1.0, reservePercent: 1.0 }
    },

    // ALLY (ChampsTPO only)
    [LOAN_PROGRAM_CODES.PROGRAM_001]: {
        CheckingAccount: { auPercent: 1.0, reservePercent: 1.0 },
        SavingsAccount: { auPercent: 1.0, reservePercent: 1.0 },
        MoneyMarketFund: { auPercent: 1.0, reservePercent: 1.0 },
        MutualFund: { auPercent: 1.0, reservePercent: 1.0 },
        MutualFunds: { auPercent: 1.0, reservePercent: 1.0 },
        Stock: { auPercent: 1.0, reservePercent: 1.0 },
        Bond: { auPercent: 1.0, reservePercent: 1.0 },
        RetirementFund: { auPercent: { under: 0.7, over: 1.0 }, reservePercent: { under: 0.8, over: 1.0 } },
        RetirementFunds: { auPercent: { under: 0.7, over: 1.0 }, reservePercent: { under: 0.8, over: 1.0 } },

        CertificateOfDepositTimeDeposit: { auPercent: 1.0, reservePercent: 1.0 },
        BridgeLoanNotDeposited: { auPercent: 1.0, reservePercent: 1.0 },
        IndividualDevelopmentAccount: { auPercent: 1.0, reservePercent: 1.0 },
        TrustAccount: { auPercent: 1.0, reservePercent: 1.0 },
        LifeInsurance: { auPercent: 1.0, reservePercent: 1.0 }
    },

    // ACTIVATOR PRIME / SUNNY SELECT
    [LOAN_PROGRAM_CODES.PROGRAM_009]: {
        CheckingAccount: { auPercent: 1.0, reservePercent: 1.0 },
        SavingsAccount: { auPercent: 1.0, reservePercent: 1.0 },
        MoneyMarketFund: { auPercent: 1.0, reservePercent: 1.0 },
        MutualFund: { auPercent: 1.0, reservePercent: 1.0 },
        MutualFunds: { auPercent: 1.0, reservePercent: 1.0 },
        Stock: { auPercent: 1.0, reservePercent: 1.0 },
        Bond: { auPercent: 1.0, reservePercent: 1.0 },
        RetirementFund: { auPercent: { under: 0.7, over: 1.0 }, reservePercent: { under: 0.8, over: 1.0 } },
        RetirementFunds: { auPercent: { under: 0.7, over: 1.0 }, reservePercent: { under: 0.8, over: 1.0 } },

        CertificateOfDepositTimeDeposit: { auPercent: 1.0, reservePercent: 1.0 },
        BridgeLoanNotDeposited: { auPercent: 1.0, reservePercent: 1.0 },
        IndividualDevelopmentAccount: { auPercent: 1.0, reservePercent: 1.0 },
        TrustAccount: { auPercent: 1.0, reservePercent: 1.0 },
        LifeInsurance: { auPercent: 1.0, reservePercent: 1.0 }
    }
};

export const FIELD_IDS = {
    verifiedAsset: "CX.VERIFIED.ASSET",
    verifiedAssetReserve: "CX.VERIFIED.ASSET.RESERVE",
    verifiedAssetDownPayment: "CX.VERIFIED.ASSET.DOWNPAYMENT",

    reservesActual: "CX.RESERVES.ACTUAL",
    reservesNoCashout: "CX.RESERVES.NOCASHOUT",

    loanProgram: "CX.LOAN.PROGRAM.IDENTIFIER",
    loanProgram_1401: "1401",
    shortTermRental: "CX.Short.Term.Rental",
    loanProductName: "CX.LOAN.PRODUCT.NAME",
    assetDeplVodJson: "CX.ASSET.DEPL.VOD.JSON",

    cashInHand_142: "142",
    ltv_353: "353",
    pitia_912: "912"
};

const DISALLOWED_PROGRAM_PREFIXES = ["CLI"];

export function normalizeYesNo(val) {
    const s = String(val ?? "").trim().toUpperCase();
    if (!s) return "";
    if (s === "Y" || s === "YES" || s === "TRUE" || s === "1") return "YES";
    if (s === "N" || s === "NO" || s === "FALSE" || s === "0") return "NO";
    return s;
}

export function isCliProgram(programCodeRaw) {
    const programCode = String(programCodeRaw ?? "").trim().toUpperCase();
    if (!programCode) return false;
    return DISALLOWED_PROGRAM_PREFIXES.some((p) => programCode.startsWith(p));
}

export function toNumber(val) {
    if (val === null || val === undefined) return 0;
    const n = Number(String(val).replace(/,/g, ""));
    return Number.isFinite(n) ? n : 0;
}

export function money(val) {
    const n = toNumber(val);
    return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function moneySigned(val) {
    const n = toNumber(val);
    const abs = Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    if (n < 0) return `$ (${abs})`;
    return `$ ${abs}`;
}

// birthDate: "YYYY-MM-DD" (or ISO). Returns decimal years (e.g. 59.6)
export function calcAgeYearsFromBirthDate(birthDate, asOfDate = new Date()) {
    if (!birthDate) return 0;

    const s = String(birthDate).trim();
    if (!s) return 0;

    const parts = s.split("T")[0].split("-");
    if (parts.length < 3) return 0;

    const y = Number(parts[0]);
    const m = Number(parts[1]);
    const d = Number(parts[2]);
    if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return 0;

    const dob = new Date(y, m - 1, d);
    if (Number.isNaN(dob.getTime())) return 0;

    const msPerDay = 24 * 60 * 60 * 1000;
    const days = (asOfDate - dob) / msPerDay;

    const years = days / 365.2425;
    return Number.isFinite(years) ? years : 0;
}

export function isOver59Half(ageYears) {
    const a = Number(ageYears);
    const age = Number.isFinite(a) ? a : 0;
    return age >= 59.5;
}

export function getRetirementLabelByAgeYears(ageYears) {
    return isOver59Half(ageYears)
        ? "401k & Retirement Accounts (over 59 ½ years old)"
        : "401k & Retirement Accounts (under 59 ½ years old)";
}

export function getAccountTypeLabel(typeToken) {
    const t = String(typeToken || "");
    if (t === "RetirementFund" || t === "RetirementFunds") return "Retirement Fund";
    return ACCOUNT_TYPE_LABEL[t] || t || "";
}

export function getAccountTypeGroupName(typeToken, ageYears) {
    const t = String(typeToken || "");

    if (t === "RetirementFund" || t === "RetirementFunds") {
        return getRetirementLabelByAgeYears(ageYears);
    }

    const grp = ACCOUNT_TYPE_GROUPS.find((g) => g.types.includes(t));
    return grp?.groupName || "";
}

function resolveRulePercent(rule, ageYears) {
    if (rule === undefined || rule === null) return 0;

    if (typeof rule === "object") {
        return isOver59Half(ageYears) ? Number(rule.over) : Number(rule.under);
    }

    const pct = Number(rule);
    return Number.isFinite(pct) ? pct : 0;
}

export function getAuAndReservePercents(typeToken, ageYears, selectedProduct) {
    const t = String(typeToken || "");
    const sel = String(selectedProduct || "").trim().toUpperCase();

    const cfg = AU_RESERVE_PERCENT_CONFIG_BY_PRODUCT_SELECT[sel];
    if (!cfg) return { auPercent: 0, reservePercent: 0 };

    const rule = cfg[t];
    if (!rule) return { auPercent: 0, reservePercent: 0 };

    const auPercent = resolveRulePercent(rule.auPercent, ageYears);
    const reservePercent = resolveRulePercent(rule.reservePercent, ageYears);

    return {
        auPercent: Number.isFinite(auPercent) ? auPercent : 0,
        reservePercent: Number.isFinite(reservePercent) ? reservePercent : 0
    };
}

export const SUPPORTED_OTHER_ASSET_TYPES = [
    "PendingNetSaleProceedsFromRealEstateAsset",
    "ProceedsFromSaleOfNonRealEstateAsset",
    "Annuity",
    "ProceedsFromSecuredLoan",
    "Other",
    "EarnestMoney",
    "EmployerAssistedHousing",
    "RelocationFunds",
    "LotEquity",
    "LeasePurchaseCredit"
];

export const OTHER_ASSET_TYPE_ALIASES = {
    // sample response has plural, requirement has singular
    PendingNetSaleProceedsFromRealEstateAssets: "PendingNetSaleProceedsFromRealEstateAsset"
};

export const OTHER_ASSET_PERCENT_BY_PRODUCT_SELECT = {
    [LOAN_PROGRAM_CODES.PROGRAM_002]: 1.0, // ACCELERATOR / BREEZY
    [LOAN_PROGRAM_CODES.PROGRAM_004]: 1.0, // ACTIVATOR / SUNNY
    [LOAN_PROGRAM_CODES.PROGRAM_006]: 0.6, // ACTIVATOR ITIN / SUNNY ITIN
    [LOAN_PROGRAM_CODES.PROGRAM_003]: 1.0, // ACCELERATOR INCOME / BREEZY INCOME
    [LOAN_PROGRAM_CODES.PROGRAM_005]: 0.6, // ACCELERATOR ITIN / BREEZY ITIN
    [LOAN_PROGRAM_CODES.PROGRAM_007]: 0.6, // AMBASSADOR / PASSPORT
    [LOAN_PROGRAM_CODES.PROGRAM_008]: 0.6, // SUPER JUMBO / STARRY
    [LOAN_PROGRAM_CODES.PROGRAM_001]: 1.0, // ALLY (ChampsTPO only)
    [LOAN_PROGRAM_CODES.PROGRAM_009]: 0.6  // ACTIVATOR PRIME / SUNNY SELECT
};

export function normalizeOtherAssetType(assetType) {
    const raw = String(assetType || "").trim();
    return OTHER_ASSET_TYPE_ALIASES[raw] || raw;
}

export function getOtherAssetPercent(assetType, selectedProduct) {
    const type = normalizeOtherAssetType(assetType);

    // Requirement: Annuity has 60%
    if (type === "Annuity") return 0.6;

    const sel = String(selectedProduct || "").trim().toUpperCase();
    const pct = OTHER_ASSET_PERCENT_BY_PRODUCT_SELECT[sel];

    return Number.isFinite(Number(pct)) ? Number(pct) : 0;
}

/**
 * Eligibility:
 * - 1401 must exist
 * - not CLI*
 * - selected product must be configured
 */
export function isEligibleForReservesCalculator({ loanProgram1401, loanProgramCode, selectedProduct }) {
    const lp1401 = String(loanProgram1401 ?? "").trim();
    const prog = String(loanProgramCode ?? "").trim();
    const prod = String(selectedProduct ?? "").trim().toUpperCase();

    if (!lp1401) {
        return { ok: false, message: "Required Reserves Calculator isn’t available until a loan program is selected." };
    }

    if (isCliProgram(prog)) {
        return { ok: false, message: "Required Reserves Calculator isn’t available for this loan program." };
    }

    if (!AU_RESERVE_PERCENT_CONFIG_BY_PRODUCT_SELECT[prod]) {
        return { ok: false, message: "Required Reserves Calculator isn’t available for this loan product." };
    }

    return { ok: true, message: "" };
}

/**
 * Build grouped tree rows for AntD Table
 */
export function buildGroupedVodRows(rows, toNumberFn) {
    const map = new Map();

    (rows || []).forEach((r) => {
        const groupName = r.groupName;
        if (!groupName) return;
        if (!map.has(groupName)) map.set(groupName, []);
        map.get(groupName).push(r);
    });

    const buildGroupInfo = (children) => {
        const gName = String(children?.[0]?.groupName || "");
        if (gName === "Cash in Hand") return "";

        const pct = Number(children?.[0]?.reservePercent ?? 0);
        if (!Number.isFinite(pct)) return "";
        if (pct < 1) return `Max usable ${Math.round(pct * 100)}% of verified asset`;
        return "No limit (can use up to verified asset)";
    };

    return Array.from(map.entries()).map(([groupName, children]) => {
        const cashValue = children.reduce((s, x) => {
            if (x.isCashInHand) return s + Math.abs(toNumberFn(x.cashInHandValue));
            return s + toNumberFn(x.cashValue);
        }, 0);

        const amountUsedAu = children.reduce((s, x) => s + toNumberFn(x.amountUsedAu), 0);
        const allowedReserve = children.reduce((s, x) => {
            // cash-in-hand subtotal column is blank anyway
            if (x.isCashInHand) return s;
            if (!x.verified) return s;
            if (!x.useForReserve) return s;

            const allowed = toNumberFn(x.allowedReserve);
            const dp = toNumberFn(x.downPaymentAmount);

            // ONLY change when both are checked
            if (x.useForReserve && x.useForDownPayment) {
                return s + Math.max(allowed - dp, 0);
            }

            return s + allowed;
        }, 0);

        const downPaymentAmount = children.reduce((s, x) => {
            // cash-in-hand not applicable
            if (x.isCashInHand) return s;
            return s + toNumberFn(x.downPaymentAmount);
        }, 0);


        const verifiedAsset = children.reduce((s, x) => {
            // cash-in-hand should not show under Verified Asset subtotal column
            if (x.isCashInHand) return s;

            return x.verified ? s + toNumberFn(x.verifiedAsset) : s;
        }, 0);

        const enhancedChildren = children.map((c, idx) => ({
            ...c,
            isLastInGroup: idx === children.length - 1
        }));

        const subtotalRow = {
            key: `subtotal__${groupName}`,
            isSubtotal: true,
            groupName,
            typeLabel: "Subtotal",
            cashValue,
            amountUsedAu,
            allowedReserve,
            verifiedAsset,
            downPaymentAmount
        };

        return {
            key: `group__${groupName}`,
            isGroup: true,
            typeLabel: groupName,
            groupInfo: buildGroupInfo(children),
            cashValue,
            amountUsedAu,
            allowedReserve,
            verifiedAsset,
            downPaymentAmount,
            children: [...enhancedChildren, subtotalRow]
        };
    });
}

/**
 * Build flat VOD grid rows
 */
export function buildVODGridRows(vods, ownerAgeYearsMap, selectedProduct) {
    const map = new Map();

    (vods || []).forEach((vod) => {
        const vodId = vod?.id;
        const items = Array.isArray(vod?.items) ? vod.items : [];
        const holderName = vod?.holderName || "";

        const owner = vod?.owner || "";
        const ageYears =
            owner === "CoBorrower"
                ? (ownerAgeYearsMap?.CoBorrower ?? 0)
                : (ownerAgeYearsMap?.Borrower ?? 0);

        items.forEach((item) => {
            const type = item?.type || "";
            const accountIdentifier = item?.accountIdentifier || "";
            const amt = toNumber(item?.urla2020CashOrMarketValueAmount);
            const depositoryAccountName = item?.depositoryAccountName || "";

            if (!type || !accountIdentifier) return;
            if (!SUPPORTED_ACCOUNT_TYPES.includes(String(type))) return;

            const key = `${vodId}__${type}__${accountIdentifier}`.toLowerCase();

            const { auPercent, reservePercent } = getAuAndReservePercents(type, ageYears, selectedProduct);
            if (!Number.isFinite(Number(auPercent)) || !Number.isFinite(Number(reservePercent))) return;

            const typeLabel = getAccountTypeLabel(type);
            const groupName = getAccountTypeGroupName(type, ageYears);
            if (!groupName) return;

            if (!map.has(key)) {
                map.set(key, {
                    key,
                    vodId,
                    type,
                    typeLabel,
                    groupName,
                    holderName: holderName + ' - ' + accountIdentifier,
                    accountIdentifier,
                    depositoryAccountName: depositoryAccountName || "",
                    cashValue: 0,
                    amountUsedAu: 0,
                    verified: false,
                    useForReserve: false,
                    useForDownPayment: false,
                    downPaymentAmount: "",
                    ageYears,
                    owner,
                    auPercent,
                    reservePercent,
                    isGift: false,
                    isCashInHand: false
                });
            }

            const row = map.get(key);

            if (!row.holderName && holderName) row.holderName = holderName;
            if (!row.depositoryAccountName && depositoryAccountName) row.depositoryAccountName = depositoryAccountName;

            if (amt > 0) row.cashValue += amt;
            if (amt < 0) row.amountUsedAu += Math.abs(amt);
        });
    });

    return Array.from(map.values()).filter((r) => toNumber(r.cashValue) > 0);
}

/**
 * Gifts/Grants rows
 */
export function buildGiftGridRows(gifts, selectedPair) {
    const list = Array.isArray(gifts) ? gifts : [];

    const borrowerName = String(selectedPair?.borrower?.fullName || "").trim();
    const coborrowerName = String(selectedPair?.coborrower?.fullName || "").trim();

    const resolveOwnerName = (ownerToken) => {
        const o = String(ownerToken || "").trim().toLowerCase();
        if (o === "borrower") return borrowerName || "Borrower";
        if (o === "coborrower") return coborrowerName || "CoBorrower";
        return String(ownerToken || "").trim();
    };

    return list
        .filter((g) => g?.id && toNumber(g?.amount) > 0)
        .map((g) => {
            const id = String(g.id);
            const key = `gift__${id}`.toLowerCase();


            const assetType = String(g.assetType || "").trim();
            const ownerToken = String(g.owner || "").trim();

            const holder = String(g.source || "").trim();


            return {
                key,
                isGift: true,
                isCashInHand: false,
                typeLabel: assetType ? `${assetType}` : "Gift",
                groupName: "Gifts/Grants",

                holderName: '-',
                depositoryAccountName: resolveOwnerName(ownerToken),

                cashValue: toNumber(g.amount),
                amountUsedAu: 0,
                verified: false,
                useForReserve: false,
                useForDownPayment: false,
                downPaymentAmount: "",
                auPercent: 1.0,
                reservePercent: 1.0,

                giftId: id,
                giftAssetType: assetType,
                giftSource: holder,
                giftOwner: ownerToken
            };
        });
}

export function buildOtherAssetGridRows(otherAssets, selectedProduct) {
    const list = Array.isArray(otherAssets) ? otherAssets : [];

    return list
        .filter((asset) => {
            const type = normalizeOtherAssetType(asset?.assetType);

            return (
                asset?.id &&
                SUPPORTED_OTHER_ASSET_TYPES.includes(type) &&
                toNumber(asset?.cashOrMarketValue) > 0
            );
        })
        .map((asset) => {
            const id = String(asset.id || "");
            const type = normalizeOtherAssetType(asset.assetType);
            const percent = getOtherAssetPercent(type, selectedProduct);

            return {
                key: `otherasset__${id}`.toLowerCase(),

                isGift: false,
                isCashInHand: false,
                isOtherAsset: true,

                otherAssetId: id,
                otherAssetType: type,

                type,
                typeLabel: ASSET_TYPE_LABEL_MAP[type] || type,

                // IMPORTANT:
                // Do not use "Other" here.
                // "Other" is for Deposit/Account types.
                groupName: "Other Asset",

                holderName: String(asset.holderName || "-").trim(),
                depositoryAccountName: String(asset.borrowerType || "").trim(),

                cashValue: toNumber(asset.cashOrMarketValue),
                amountUsedAu: 0,

                verified: false,
                useForReserve: false,
                useForDownPayment: false,
                downPaymentAmount: "",

                auPercent: percent,
                reservePercent: percent,

                accountIdentifier: "",
                owner: String(asset.borrowerType || "").trim()
            };
        })
        .filter((row) => Number(row.reservePercent) > 0);
}

/**
 * Cash-in-hand rows (142 negative)
 */
export function buildCashInHandRows(field142Value, selectedProduct) {
    const v = toNumber(field142Value);
    if (!(v < 0)) return [];
    const pct = getCashInHandAllowedPercent(selectedProduct);

    return [
        {
            key: "cashinhand__142",
            isGift: false,
            isCashInHand: true,
            typeLabel: "Cash in Hand",
            groupName: "Cash in Hand",
            holderName: "",
            depositoryAccountName: "",
            cashValue: 0,
            amountUsedAu: 0,
            verified: false,
            useForReserve: true,
            useForDownPayment: false,
            auPercent: 0,
            reservePercent: pct,
            cashInHandValue: v
        }
    ];
}

/**
 * JSON storage (CX.ASSET.DEPL.VOD.JSON)
 * - applications[].reserves.rows includes cash-in-hand with useForReserve
 * - applications[].cashInHand uses useForReserve
 */
export function buildReservesJsonMerge(
    existingJson,
    applicationId,
    rows,
    giftsRaw,
    cashInHand142,
    cashInHandUseForReserve,
    otherAssetsRaw = []
) {
    const nowIso = new Date().toISOString();

    const base =
        existingJson && typeof existingJson === "object"
            ? existingJson
            : { version: 1, lastUpdated: nowIso, applications: [] };

    const apps = Array.isArray(base.applications) ? [...base.applications] : [];
    const idx = apps.findIndex((a) => a.applicationId === applicationId);

    const prevApp =
        idx >= 0 && apps[idx] && typeof apps[idx] === "object"
            ? apps[idx]
            : { applicationId };

    const reserveRows = (rows || [])
        .filter((r) => !r.isGroup && !r.isSubtotal)
        .map((r) => {
            if (r.isCashInHand) {
                return {
                    key: r.key,
                    isCashInHand: true,
                    amount: toNumber(r.cashInHandValue),
                    useForReserve: !!r.useForReserve
                };
            }

            if (r.isGift) {
                return {
                    key: r.key,
                    isGift: true,
                    giftId: String(r.giftId || ""),
                    amount: toNumber(r.cashValue),
                    verified: !!r.verified,
                    useForReserve: !!r.useForReserve,
                    useForDownPayment: !!r.useForDownPayment,
                    downPaymentAmount: toNumber(r.downPaymentAmount)
                };
            }

            if (r.isOtherAsset) {
                return {
                    key: r.key,
                    isOtherAsset: true,
                    otherAssetId: String(r.otherAssetId || ""),
                    assetType: String(r.otherAssetType || r.type || ""),
                    amount: toNumber(r.cashValue),
                    verified: !!r.verified,
                    useForReserve: !!r.useForReserve,
                    useForDownPayment: !!r.useForDownPayment,
                    downPaymentAmount: toNumber(r.downPaymentAmount)
                };
            }

            return {
                key: r.key,
                verified: !!r.verified,
                useForReserve: !!r.useForReserve,
                useForDownPayment: !!r.useForDownPayment,
                downPaymentAmount: toNumber(r.downPaymentAmount)
            };
        });

    const giftsList = (Array.isArray(giftsRaw) ? giftsRaw : []).map((g) => {
        const id = String(g?.id || "");
        const key = `gift__${id}`.toLowerCase();
        const saved = reserveRows.find((x) => x.key === key) || {};

        return {
            id,
            assetType: String(g?.assetType || ""),
            source: String(g?.source || ""),
            owner: String(g?.owner || ""),
            amount: toNumber(g?.amount),
            verified: !!saved.verified,
            useForReserve: !!saved.useForReserve,
            useForDownPayment: !!saved.useForDownPayment,
            downPaymentAmount: toNumber(saved.downPaymentAmount)
        };
    });

    const otherAssetsList = (Array.isArray(otherAssetsRaw) ? otherAssetsRaw : []).map((a) => {
        const id = String(a?.id || "");
        const key = `otherasset__${id}`.toLowerCase();
        const saved = reserveRows.find((x) => x.key === key) || {};

        return {
            id,
            assetType: normalizeOtherAssetType(a?.assetType),
            borrowerType: String(a?.borrowerType || ""),
            holderName: String(a?.holderName || ""),
            cashOrMarketValue: toNumber(a?.cashOrMarketValue),
            verified: !!saved.verified,
            useForReserve: !!saved.useForReserve,
            useForDownPayment: !!saved.useForDownPayment,
            downPaymentAmount: toNumber(saved.downPaymentAmount)
        };
    });

    const cashInHandAmount = toNumber(cashInHand142);
    const cashInHandNode =
        cashInHandAmount < 0
            ? {
                amount: cashInHandAmount,
                useForReserve: !!cashInHandUseForReserve,
                lastUpdated: nowIso
            }
            : null;

    const mergedApp = {
        ...prevApp,
        applicationId,
        reserves: {
            lastUpdated: nowIso,
            rows: reserveRows
        },
        gifts: giftsList,
        otherAssets: otherAssetsList,
        cashInHand: cashInHandNode
    };

    if (idx >= 0) apps[idx] = mergedApp;
    else apps.push(mergedApp);

    return {
        ...base,
        version: base.version || 1,
        lastUpdated: nowIso,
        applications: apps
    };
}

export function readReservesFlagsMapFromJson(jsonObj, applicationId) {
    const map = {};
    if (!jsonObj || typeof jsonObj !== "object") return map;

    const apps = Array.isArray(jsonObj.applications) ? jsonObj.applications : [];
    const app = apps.find((a) => a?.applicationId === applicationId);
    const rows = app?.reserves?.rows;
    if (!Array.isArray(rows)) return map;

    rows.forEach((r) => {
        const k = String(r?.key || "");
        if (!k) return;

        map[k] = {
            verified: !!r?.verified,
            useForReserve: !!r?.useForReserve,
            useForDownPayment: !!r?.useForDownPayment,
            downPaymentAmount: toNumber(r?.downPaymentAmount)
        };
    });

    return map;
}

export function readCashInHandFromJson(jsonObj, applicationId) {
    if (!jsonObj || typeof jsonObj !== "object") return null;
    const apps = Array.isArray(jsonObj.applications) ? jsonObj.applications : [];
    const app = apps.find((a) => a?.applicationId === applicationId);
    const node = app?.cashInHand;
    if (!node || typeof node !== "object") return null;
    return node;
}

// ------------------------------
// Shared recalculation helpers
// ------------------------------

export const round1 = (n) => Math.max(0, Math.round(toNumber(n) * 10) / 10);

/**
 * SAME logic as RequiredReservesPage.calcReserveMonths
 * returns { actual: "", noCashout: "" } when PITIA missing or not allowed
 */
export function calcReserveMonths({
    productName,
    programId,
    ltv,
    verifiedAssetsReserve,
    verifiedAssetsDownPayment,
    cashFromToBorrower,
    pitia
}) {

    const pit = toNumber(pitia);
    if (!pit) return { actual: "", noCashout: "" };

    const vReserve = toNumber(verifiedAssetsReserve) + toNumber(verifiedAssetsDownPayment);
    const cash142 = toNumber(cashFromToBorrower);
    const ltvVal = toNumber(ltv);

    // Formula A
    const formulaA = (vReserve - cash142) / pit;

    // Formula A-1 (If 142 is Negative -> vReserve/912 else Formula A)
    const formulaA1 = cash142 < 0 ? (vReserve / pit) : formulaA;

    // Formula B (no cash-out months)
    const monthReserveNotFromCashout =
            cash142 < 0
                ? ((vReserve + cash142) / pit)
                : ((vReserve - cash142) / pit);

    const nonCashoutMonths = round1(monthReserveNotFromCashout);

    const prog = String(programId || "").trim().toUpperCase();

    /*
     * These used to be substring tests against the ChampsTPO brand name, which
     * caught both ALLY products and any "SUPER JUMBO*" variant. Every product
     * now has a canonical code, so match on the code instead - a brand-name
     * substring can only ever match one company's vocabulary.
     */
    const isALLY = isLoanProgram(productName, [
        LOAN_PROGRAM_CODES.PROGRAM_001, // ALLY (ChampsTPO only)
        LOAN_PROGRAM_CODES.PROGRAM_011  // ALLY EQUITY FIRST (ChampsTPO only)
    ]);

    const isSJ = isLoanProgram(productName, LOAN_PROGRAM_CODES.PROGRAM_008); // SUPER JUMBO / STARRY

    const isBPACC58 = prog === "BPACC58";
    const isBITIN = prog === "BITIN";

    let monthActualReserve;

    if (isSJ || isBPACC58) {
        monthActualReserve = formulaA1;
    } else if (isALLY) {
        if (ltvVal <= 50) {
            monthActualReserve = formulaA1;
        } else {
            if (nonCashoutMonths >= 2) monthActualReserve = formulaA1;
            else return { actual: "", noCashout: String(nonCashoutMonths) };
        }
    } else if (isBITIN) {
        if (nonCashoutMonths >= 3) monthActualReserve = formulaA1;
        else return { actual: "", noCashout: String(nonCashoutMonths) };
    } else {
        monthActualReserve = formulaA1;
    }

    const actualMonths = round1(monthActualReserve);

    return {
        actual: String(actualMonths),
        noCashout: String(nonCashoutMonths)
    };
}

/**
 * Reserve allocation amount (SAME behavior as RequiredReservesPage.getReserveAllocationAmount)
 */
export function getReserveAllocationAmount(row) {
    if (!row) return 0;
    if (row.isGroup || row.isSubtotal) return 0;
    if (row.isGift) return 0;

    // cash-in-hand: allowedReserve already computed from abs(142)*pct, and checkbox is forced true
    if (row.isCashInHand) {
        return row.useForReserve ? toNumber(row.allowedReserve) : 0;
    }

    // must be verified to count
    if (!row.verified) return 0;

    const allowed = toNumber(row.allowedReserve);
    const dp = toNumber(row.downPaymentAmount);

    if (row.useForReserve) {
        if (row.useForDownPayment) return Math.max(allowed - dp, 0);
        return allowed;
    }

    // Down Payment only => add DP into reserves total (your current rule)
    if (row.useForDownPayment) return Math.max(dp, 0);

    return 0;
}

/**
 * Recalculate (Verified Asset, Verified Asset for Reserves, Down Payment, reserve months)
 * using:
 * - current VOD negatives (AU Amount) from VOD items
 * - gifts
 * - cash-in-hand (142 negative)
 * - saved JSON flags (verified/reserves/downpayment + downPaymentAmount)
 *
 * Returns: { totals, months }
 */
export function recalcReservesFromData({
    vodsRaw,
    giftsRaw,
    selectedPair,
    cash142,
    selectedProduct,
    applicationId,
    savedJsonObj,
    ltv353,
    pitia912,
    loanProgramCode
}) {
    const sel = String(selectedProduct || "");
    const appId = String(applicationId || "");

    // Build base rows
    const ownerAgeYearsMap = {
        Borrower: selectedPair?.borrower?.birthDate ? calcAgeYearsFromBirthDate(selectedPair.borrower.birthDate) : 0,
        CoBorrower: selectedPair?.coborrower?.birthDate ? calcAgeYearsFromBirthDate(selectedPair.coborrower.birthDate) : 0
    };

    const vodRows = buildVODGridRows(Array.isArray(vodsRaw) ? vodsRaw : [], ownerAgeYearsMap, sel);
    const giftRows = buildGiftGridRows(Array.isArray(giftsRaw) ? giftsRaw : [], selectedPair || null);
    const cashRows = buildCashInHandRows(cash142, sel); // only if 142 negative

    const allRows = [...vodRows, ...giftRows, ...cashRows];

    // Restore flags from JSON
    const flagsMap = readReservesFlagsMapFromJson(savedJsonObj, appId);

    const restored = allRows.map((r) => {
        const saved = flagsMap[r.key];

        if (r.isCashInHand) {
            return {
                ...r,
                verified: false,
                useForReserve: true,
                useForDownPayment: false,
                downPaymentAmount: ""
            };
        }

        if (r.isGift) {
            const verified = !!saved?.verified;
            const useForDownPayment = !!saved?.useForDownPayment;
            const savedAmt = toNumber(saved?.downPaymentAmount);

            return {
                ...r,
                verified,
                useForReserve: false,
                useForDownPayment,
                downPaymentAmount: useForDownPayment ? (savedAmt > 0 ? money(savedAmt) : money(toNumber(r.cashValue))) : ""
            };
        }

        const verified = !!saved?.verified;
        const useForReserve = !!saved?.useForReserve;
        const useForDownPayment = !!saved?.useForDownPayment;
        const savedAmt = toNumber(saved?.downPaymentAmount);

        return {
            ...r,
            verified,
            useForReserve,
            useForDownPayment,
            downPaymentAmount: useForDownPayment ? (savedAmt > 0 ? money(savedAmt) : money(toNumber(r.cashValue))) : ""
        };
    });

    // Compute verifiedAsset + allowedReserve like RequiredReservesPage.computedRows
    const computedRows = restored.map((r) => {
        const reservePct = Number(r.reservePercent ?? 0);
        const reservePctSafe = Number.isFinite(reservePct) ? reservePct : 0;

        if (r.isCashInHand) {
            const cashAbs = Math.abs(toNumber(r.cashInHandValue));
            const allowedReserve = cashAbs > 0 ? (cashAbs * reservePctSafe) : 0;
            return { ...r, verifiedAsset: 0, allowedReserve };
        }

        const cash = toNumber(r.cashValue);
        const auAmount = toNumber(r.amountUsedAu);
        const auPct = Number(r.auPercent ?? 0);
        const auPctSafe = Number.isFinite(auPct) ? auPct : 0;

        const base = auAmount > 0 && auPctSafe > 0 ? cash - auAmount / auPctSafe : cash;
        const verifiedAsset = Math.max(toNumber(base), 0);

        const allowedReserve = r.verified ? verifiedAsset * reservePctSafe : 0;

        return { ...r, verifiedAsset, allowedReserve };
    });

    // Totals
    const totalVerifiedAsset = computedRows.reduce((s, r) => {
        if (r.isGroup || r.isSubtotal) return s;
        if (r.isCashInHand) return s;
        return r.verified ? s + toNumber(r.verifiedAsset) : s;
    }, 0);

    const totalVerifiedAssetReserve = computedRows.reduce((s, r) => {
        if (r.isGroup || r.isSubtotal) return s;
        return s + getReserveAllocationAmount(r);
    }, 0);

    const totalVerifiedAssetDownPayment = computedRows.reduce((s, r) => {
        if (r.isGroup || r.isSubtotal) return s;
        if (r.isCashInHand) return s;
        if (!r.useForDownPayment) return s;
        if (!r.verified) return s;

        const entered = toNumber(r.downPaymentAmount);
        return entered > 0 ? s + entered : s;
    }, 0);

    const months = calcReserveMonths({
        productName: sel,
        programId: loanProgramCode,
        ltv: ltv353,
        verifiedAssetsReserve: totalVerifiedAssetReserve,
        verifiedAssetsDownPayment: totalVerifiedAssetDownPayment,
        cashFromToBorrower: cash142,
        pitia: pitia912
    });

    return {
        totals: {
            verifiedAsset: totalVerifiedAsset,
            verifiedAssetReserve: totalVerifiedAssetReserve,
            verifiedAssetDownPayment: totalVerifiedAssetDownPayment
        },
        months
    };
}